import { Loader, Stack, Text } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { isApiError, isSessionExpired } from "../../api/errors";
import { useGetMe, useSignOut } from "../../api/generated/endpoints/identity/identity";
import { useAcceptInvitation } from "../../api/generated/endpoints/teams/teams";
import { InvitationAccept } from "../../api/generated/zod";
import { accessScopeQuery } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { forgetMediaAccess } from "../../platform/media/access";
import { AuthPrimaryButton, AuthSecondaryButton, AuthShell } from "../../ui/AuthShell";
import { SignInChoices } from "./SignInChoices";

const TITLE = "Join a team on Created.ai";

/** The token from `#token=...`. A fragment never reaches a server, so it stays in this tab. */
function tokenOf(hash: string): string | undefined {
  const parsed = InvitationAccept.safeParse({ token: new URLSearchParams(hash).get("token") });
  return parsed.success ? parsed.data.token : undefined;
}

/**
 * The invitation link from the email (spec teams/invite-by-email). Opening it is the click:
 * signed in, the token is posted once and the team opens; signed out, the user signs in on this
 * page and then it accepts. The token goes only in the accept call and, through Google, in its
 * sign-in attempt: never in a query string or in storage.
 */
export function AcceptInvitationScreen() {
  const hash = useRouterState({ select: (state) => state.location.hash });
  const token = tokenOf(hash);
  const scope = useQuery(accessScopeQuery());
  if (token === undefined) {
    return (
      <AuthShell title={TITLE}>
        <Text size="sm">
          This invitation link is incomplete. Open it again from the email, or copy the whole link
          into the address bar.
        </Text>
      </AuthShell>
    );
  }
  if (scope.isPending) {
    return (
      <AuthShell title={TITLE}>
        <Loader size="sm" aria-label="Checking your sign-in" />
      </AuthShell>
    );
  }
  if (scope.isError && !isSessionExpired(scope.error)) {
    return (
      <AuthShell title={TITLE}>
        <ErrorAlert error={scope.error} />
        <AuthPrimaryButton onClick={() => void scope.refetch()}>Try again</AuthPrimaryButton>
      </AuthShell>
    );
  }
  if (scope.isError) return <SignInFirst hash={hash} />;
  // One accept per token: a new link opened in this tab changes only the fragment, so the page
  // stays mounted, and its token gets a post and a result of its own.
  return <Accept key={token} token={token} />;
}

function SignInFirst({ hash }: { hash: string }) {
  const queryClient = useQueryClient();
  // A new session: every cached answer is dropped and read again (audit frontend B.9), and the
  // scope's brings the accept.
  const onSignedIn = () => {
    forgetMediaAccess();
    void queryClient.resetQueries();
  };
  // Live's words for an invitee (frontend `registration-form/constants.ts`), without its "An
  // account with this email already exists."
  return (
    <AuthShell title="Sign in to accept your invitation">
      <Text size="sm">Continue with Google or a code sent to your email.</Text>
      {/* Google's way back is this link, fragment and all (decision 6). */}
      <SignInChoices
        redirect={`/invitations/accept#${hash}`}
        divider="or sign in with email"
        onSignedIn={onSignedIn}
      />
    </AuthShell>
  );
}

function Accept({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const me = useGetMe();
  const accept = useAcceptInvitation({
    mutation: {
      onSuccess: async ({ team_id }) => {
        // A new membership: the scope and everything cached under the old one are read again.
        queryClient.clear();
        await navigate({ to: "/teams/$teamId", params: { teamId: team_id }, replace: true });
      },
    },
  });
  // Opening the link is the click (decision 1): one post per signed-in visit, however often
  // this renders.
  const { mutate } = accept;
  const posted = useRef(false);
  useEffect(() => {
    if (posted.current) return;
    posted.current = true;
    mutate({ data: { token } });
  }, [mutate, token]);
  // Signing out keeps this tab and its token. Resetting drops every cached answer and reads the
  // scope again, whose 401 brings back the sign-in.
  const signOut = useSignOut({
    mutation: {
      onSuccess: () => {
        forgetMediaAccess();
        return queryClient.resetQueries();
      },
    },
  });
  if (accept.isError && isApiError(accept.error, "NOT_FOUND")) {
    // Live's words (`components/invite-invalid`), then ours: the accept is bound to one email.
    return (
      <AuthShell title="Invitation Not Valid">
        <Stack gap="xs" role="alert">
          <Text size="sm" fw={600}>
            This invitation is no longer valid.
          </Text>
          <Text size="sm">
            The invitation link may have expired, been revoked, or already been used. Please
            contact your administrator for a new invitation.
          </Text>
          <Text size="sm">
            It works only for the email address it was sent to, and you are signed in as{" "}
            <strong>{me.data?.email ?? "..."}</strong>.
          </Text>
        </Stack>
        <AuthSecondaryButton onClick={() => signOut.mutate()} loading={signOut.isPending}>
          Sign out and use another email
        </AuthSecondaryButton>
      </AuthShell>
    );
  }
  if (accept.isError && isApiError(accept.error, "ALREADY_MEMBER")) {
    return (
      <AuthShell title={TITLE}>
        <Text size="sm" role="alert">
          You are already a member of this team.
        </Text>
        <AuthSecondaryButton component={Link} to="/">
          Go to your teams
        </AuthSecondaryButton>
      </AuthShell>
    );
  }
  if (accept.isError) {
    return (
      <AuthShell title={TITLE}>
        <ErrorAlert error={accept.error} />
        <AuthPrimaryButton onClick={() => mutate({ data: { token } })}>Try again</AuthPrimaryButton>
      </AuthShell>
    );
  }
  return (
    <AuthShell title={TITLE}>
      <Stack align="center" gap="sm">
        <Loader size="sm" aria-hidden />
        {/* Live's words while its invitation is checked. */}
        <Text size="sm">Validating your invitation...</Text>
      </Stack>
    </AuthShell>
  );
}
